"use client";

const ENABLE_ELECTIVE_SELECTION_DIALOG = false;

// The button is redundant now that results calculate themselves — on page load
// and 100ms after each grade click — so it stays hidden behind this flag.
const ENABLE_CALCULATE_BUTTON = false;

import React from "react";
import { Button } from "@/components/ui/button";
import { AddNewElective } from "@/components/AddNewElective";
import { GradeSelector } from "@/components/GradeSelector";
import { ElectiveSelectionDialog } from "@/components/ElectiveSelectionDialog";
import { getSubjectCategory } from "@/lib/utils/subjectCategories";
import { getSubjectDisplayName } from "@/lib/data/subjects/subjects";
import { type ScoreTriple, getGradeOptionsForSubject, CORE_SUBJECTS } from "@/common";

interface SubjectCategory {
    name: string;
    subjects: Array<{ id: number; subject: string; abbreviation: string | string[] }>;
}

interface DseResultsSectionProps {
    translate: any;
    language: string;
    removingSubjects?: Set<string>;
    selectedElectives: string[];
    maxElectives: number;
    scores: Record<string, ScoreTriple>;
    csdGrades: string[];
    categoryAGrades: string[];
    /** Saved grades are still loading — the grade buttons hold their layout but stay unpainted. */
    gradesPending?: boolean;
    handleOpenElectiveDialog: () => void;
    setGrade: (subject: string, field: "min" | "current" | "max", grade: string) => void;
    removeElective: (subject: string) => void;
    handleAddSubject: (subject: string) => void;
    onCalculate: () => void;
    // ElectiveSelectionDialog props
    showElectiveDialog: boolean;
    handleCloseElectiveDialog: () => void;
    handleApplyElectiveChanges: () => void;
    tempSelectedElectives: string[];
    groupedSubjects: SubjectCategory[];
    isElectiveSelected: (subject: { id: number; subject: string; abbreviation: string | string[] }) => boolean;
    toggleElective: (subject: { id: number; subject: string; abbreviation: string | string[] }) => void;
    isElectiveDisabled: (subject: { id: number; subject: string; abbreviation: string | string[] }) => boolean;
}

export const DseResultsSection: React.FC<DseResultsSectionProps> = ({
    translate,
    language,
    selectedElectives,
    maxElectives,
    scores,
    csdGrades,
    categoryAGrades,
    gradesPending = false,
    handleOpenElectiveDialog,
    setGrade,
    removeElective,
    handleAddSubject,
    onCalculate,
    showElectiveDialog,
    handleCloseElectiveDialog,
    handleApplyElectiveChanges,
    tempSelectedElectives,
    groupedSubjects,
    isElectiveSelected,
    toggleElective,
    isElectiveDisabled,
}) => {
    return (
        <div id="div-dse-result" className="relative z-10 mb-6 px-4 md:px-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-1 gap-3">
                <div className="block">
                    <h2 className="ml-2 text-xl md:text-2xl font-semibold">{translate("dseResults")}</h2>
                    <p className="ml-2 text-xs md:text-sm text-gray-600 mt-1">
                        {translate("enterScoresDescription")}
                    </p>
                </div>
                {ENABLE_ELECTIVE_SELECTION_DIALOG && (
                    <div id="div-upper-btns" data-no-capture="true" className="block w-full gap-2 text-center md:w-auto md:flex md:flex-row p-0 grid grid-cols-2">
                        <Button
                            onClick={handleOpenElectiveDialog}
                            size="sm"
                            variant="secondary"
                            className="w-full sm:w-auto whitespace-nowrap cursor-pointer"
                        >
                            {translate("selectElectives")} ({selectedElectives.length}/{maxElectives})
                        </Button>
                    </div>
                )}
            </div>

            {/* Desktop Table View (Wide) */}
            <div className="hidden lg:block overflow-x-auto border rounded-lg bg-white/80 backdrop-blur-sm shadow-lg">
                <table id="tbl-dse-result-desktop" className="w-full table-auto border-collapse">

                    <thead className="bg-gradient-to-r from-rose-500 to-orange-500 text-white text-sm">
                        <tr>
                            <th className="p-3 text-left font-semibold pl-2" style={{ paddingLeft: '77px' }}>
                                {translate("subject")}
                            </th>
                            <th className="p-3 text-center font-semibold">{translate("current")}</th>
                            <th className="p-3 text-center font-semibold w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Core Subjects */}
                        {CORE_SUBJECTS.map((c, index) => {
                            const subjectName = c.subject;
                            const score = scores[subjectName] ?? { current: "", min: "", max: "" };
                            const isCSD = subjectName === "Citizenship and Social Development";
                            const grades = isCSD ? csdGrades : categoryAGrades;

                            return (

                                <tr key={subjectName} className={`border-b transition-colors hover:shadow-md ${index % 2 === 0 ? 'bg-white' : 'bg-orange-50/30'}`}>
                                    <td className="p-3 font-medium text-base">
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-0.5 rounded text-xs font-medium w-14 text-center bg-orange-100 text-orange-700">{translate("core")}</span>
                                            {getSubjectDisplayName(subjectName, language)}
                                        </div>
                                    </td>

                                    <td className="p-3 min-w-[22rem]">
                                        <GradeSelector
                                            pending={gradesPending}
                                            subjectName={subjectName}
                                            grades={grades}
                                            value={score.current}
                                            language={language}
                                            onSelect={(grade) => setGrade(subjectName, "current", grade)}
                                        />
                                    </td>
                                    <td className="p-3">
                                        <div className="w-4"></div>
                                    </td>
                                </tr>
                            );
                        })}

                        {/* Selected Elective Subjects */}
                        {selectedElectives.map((subjectName) => {
                            const score = scores[subjectName] ?? { current: "", min: "", max: "" };
                            const grades = getGradeOptionsForSubject(subjectName);
                            const category = getSubjectCategory(subjectName);

                            return (
                                <tr key={subjectName} className="border-b hover:bg-gray-50 bg-blue-50/30">
                                    <td className="p-3 font-medium">
                                        <div className="flex items-center gap-2 text-base">
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium w-14 text-center ${category === 'A' ? 'bg-blue-100 text-blue-700' :
                                                category === 'B' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-purple-100 text-purple-700'
                                                }`}>
                                                Cat {category}
                                            </span>
                                            {getSubjectDisplayName(subjectName, language)}
                                        </div>
                                    </td>
                                    <td className="p-3 min-w-[22rem]">
                                        <GradeSelector
                                            pending={gradesPending}
                                            subjectName={subjectName}
                                            grades={grades}
                                            value={score.current}
                                            language={language}
                                            onSelect={(grade) => setGrade(subjectName, "current", grade)}
                                        />
                                    </td>
                                    <td className="p-3">
                                        <button
                                            data-no-capture="true"
                                            onClick={() => removeElective(subjectName)}
                                            className="cursor-pointer text-red-500 hover:text-red-700 transition-colors"
                                            style={{ marginLeft: '-8px' }}  // Replaces -ml-1
                                            title="Remove Elective"
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile Table View (Narrow) */}
            <div className="block lg:hidden overflow-x-auto border rounded-lg bg-white/80 backdrop-blur-sm shadow-lg">
                <table id="tbl-dse-results" className="w-full table-auto border-collapse">
                    <thead className="bg-gradient-to-r from-rose-500 to-orange-500 text-white text-sm">
                        <tr>
                            <th className="p-3 text-left w-16">&nbsp;</th>
                            <th className="p-3 text-right font-semibold">&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Core Subjects */}
                        {CORE_SUBJECTS.map((c, index) => {
                            const subjectName = c.subject;
                            const score = scores[subjectName] ?? { current: "", min: "", max: "" };
                            const isCSD = subjectName === "Citizenship and Social Development";
                            const grades = isCSD ? csdGrades : categoryAGrades;

                            return (
                                <React.Fragment key={subjectName}>

                                    <tr className="transition-colors hover:shadow-md bg-red-100">
                                        <td colSpan={2} className="p-3 font-medium text-base bg-red-100">
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded text-xs font-medium w-14 text-center bg-orange-100 text-orange-700">{translate("core")}</span>
                                                {getSubjectDisplayName(subjectName, language)}
                                            </div>
                                        </td>
                                    </tr>

                                    <tr className="bg-red-20">
                                        <td colSpan={2} className="px-3 pt-1 pb-2">
                                            <GradeSelector
                                                compact
                                                pending={gradesPending}
                                                subjectName={subjectName}
                                                grades={grades}
                                                value={score.current}
                                                language={language}
                                                onSelect={(grade) => setGrade(subjectName, "current", grade)}
                                            />
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}

                        {/* Selected Elective Subjects */}
                        {selectedElectives.map((subjectName) => {
                            const score = scores[subjectName] ?? { current: "", min: "", max: "" };
                            const grades = getGradeOptionsForSubject(subjectName);
                            const category = getSubjectCategory(subjectName);

                            return (
                                <React.Fragment key={subjectName}>

                                    <tr className="border-b hover:bg-gray-50 bg-red-100">
                                        <td colSpan={2} className="p-3 font-medium text-base">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium w-14 text-center ${category === 'A' ? 'bg-blue-100 text-blue-700' :
                                                        category === 'B' ? 'bg-amber-100 text-amber-700' :
                                                            'bg-purple-100 text-purple-700'
                                                        }`}>
                                                        Cat {category}
                                                    </span>
                                                    {getSubjectDisplayName(subjectName, language)}
                                                </div>
                                                <button
                                                    data-no-capture="true"
                                                    onClick={() => removeElective(subjectName)}
                                                    className="cursor-pointer text-red-500 hover:text-red-700 transition-colors text-lg"
                                                    style={{ marginLeft: '-8px' }}  // Replaces -ml-1
                                                    title="Remove Elective"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr className="bg-red-20">
                                        <td colSpan={2} className="px-3 pt-1 pb-2">
                                            <GradeSelector
                                                compact
                                                pending={gradesPending}
                                                subjectName={subjectName}
                                                grades={grades}
                                                value={score.current}
                                                language={language}
                                                onSelect={(grade) => setGrade(subjectName, "current", grade)}
                                            />
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Subject Selection UI (below table) */}
            <div data-no-capture="true">
                <AddNewElective
                    selectedElectives={selectedElectives}
                    onAddSubject={handleAddSubject}
                    language={language}
                    translate={translate}
                    className="mt-4"
                />
            </div>

            {/* Calculate Results Button */}
            {ENABLE_CALCULATE_BUTTON && (
                <Button
                    onClick={onCalculate}
                    className="w-full mt-4 bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white font-semibold py-3 text-base cursor-pointer"
                    size="lg"
                >
                    {translate("calculateScores")}
                </Button>
            )}

            {/* Elective Selection Dialog */}
            {
                ENABLE_ELECTIVE_SELECTION_DIALOG && (
                    <ElectiveSelectionDialog
                        open={showElectiveDialog}
                        onClose={handleCloseElectiveDialog}
                        onApply={handleApplyElectiveChanges}
                        tempSelectedElectives={tempSelectedElectives}
                        maxElectives={maxElectives}
                        groupedSubjects={groupedSubjects}
                        isElectiveSelected={isElectiveSelected}
                        toggleElective={toggleElective}
                        isElectiveDisabled={isElectiveDisabled}
                        translate={translate}
                        language={language}
                    />
                )
            }
        </div >
    );
};
